// Display-only ways to shorten what a long answer shows at first glance (2026-09-30).
// Nothing is removed: each function returns segments {kind: 'main'|'fold', md, title?, danger?}
// and the chat renders 'fold' segments as <details>. Pure string work, no React.
//
//  collapseLongAnswer  (A1)  over `threshold` visible words: keep the first group open, fold
//                            the rest — groups = standalone heading lines; without headings,
//                            bullets after the first `keepBullets`.
//  foldLeadInWarnings  (A3)  single-fact question: a paragraph opening with a bold
//                            "Wichtig:" / "WARNUNG:" / "Hinweis:" label is folded (red if GEFAHR).
// (A2, merging citation markers into ranges, was dropped: every marker is its own clickable
//  [S.page] pill and adjacent same-page pills are already merged by Chat.jsx.)

const HEADING_LINE_RX = /^\s{0,3}(?:#{1,6}\s+(.+?)\s*#*|\*\*([^*\n]{1,100})\*\*:?)\s*$/;
const BULLET_RX = /^\s{0,3}[-*]\s+/;
const DANGER_RX = /\b(?:GEFAHR|LEBENSGEFAHR)\b|Lebensgefahr|\bTod\b/;
const LEADIN_RX = /^\s*\*\*(?:Wichtig|WARNUNG|Warnung|Hinweis|Achtung|ACHTUNG|Vorsicht|VORSICHT)\s*:?\*\*:?\s+\S/;
const LOOKUP_Q_RX = /^\s*(?:wie\s?viel\w*|wie\s+(?:groß|hoch|lang|schwer|viel)\w*|welche[rsnm]?\s+\S+(?:\s+\S+){0,3}\s+(?:hat|ist|beträgt|gilt|braucht)\b|welche[rsnm]?\s+(?:druck|leistung|größe|spannung|drehzahl|öl|typ|gewicht)\w*)/i;

export const visibleWords = (md) => (md || '').replace(/⟦[^⟧]*⟧/g, '').split(/\s+/).filter(Boolean).length;
const headText = (line) => { const m = HEADING_LINE_RX.exec(line); return m ? (m[1] || m[2] || '').trim() : null; };

export function isLookupQuestion(q) { return LOOKUP_Q_RX.test(q || ''); }

// Name what is inside the fold. Structural headings that repeat under every group
// ("Täglich", "Wöchentlich", … under each machine) say nothing about WHICH groups are
// folded; the headings that occur once (machine names, "Sicherheitsrelevante …") do.
// Falls back to the first three headings when nothing is unique.
export function foldTitle(restGroups, words) {
  const titles = restGroups.map((g) => headText(g.split('\n')[0])).filter(Boolean);
  const count = new Map();
  for (const t of titles) count.set(t, (count.get(t) || 0) + 1);
  let names = titles.filter((t) => count.get(t) === 1);
  let more = names.length > 5;
  names = names.slice(0, 5);
  if (!names.length) { names = [...new Set(titles)].slice(0, 3); more = new Set(titles).size > 3; }
  return `Weitere ${restGroups.length} Abschnitte${names.length ? ': ' + names.join(', ') + (more ? ' …' : '') : ''} (${words} Wörter)`;
}

// "Weitere 14 Punkte" says nothing about the content: add the first words of the first two
// folded bullets ("… u. a.: „Beim Steinert-Magnetscheider ist nach …“, „Für die Jöst- …“").
function bulletFoldTitle(tail, n) {
  const firsts = tail.split('\n').filter((l) => BULLET_RX.test(l)).slice(0, 2).map((l) => {
    const words = l.replace(BULLET_RX, '').replace(/⟦[^⟧]*⟧/g, '').replace(/[*_`]/g, '').trim().split(/\s+/).slice(0, 6).join(' ');
    return words ? `„${words} …“` : '';
  }).filter(Boolean);
  return `Weitere ${n} Punkte${firsts.length ? ', u. a.: ' + firsts.join(', ') : ''} (${visibleWords(tail)} Wörter)`;
}

const STEPS_RX = /^\s{0,3}\d+[.)]\s+\S/gm;

export function collapseLongAnswer(md, { threshold = 220, openBudget = 150, keepBullets = 8 } = {}) {
  if (typeof md !== 'string' || visibleWords(md) <= threshold) return [{ kind: 'main', md: md || '' }];
  // Step-by-step content (numbered steps / fault causes) is the working part of an answer:
  // never fold it. Only list-like answers (bullets, grouped by heading) are collapsed.
  if ((md.match(STEPS_RX) || []).length >= 3) return [{ kind: 'main', md }];
  const lines = md.split('\n');
  const heads = lines.map((l, i) => (headText(l) !== null ? i : -1)).filter((i) => i >= 0);
  if (heads.length >= 2) {
    // groups start at each heading; text before the first heading belongs to group 0
    const starts = heads[0] === 0 ? heads : [0, ...heads];
    const groups = starts.map((st, k) => lines.slice(st, (starts[k + 1] ?? lines.length)).join('\n').trimEnd());
    // keep groups open until ~openBudget words are visible, ONE fold for the rest
    let open = 0; let cut = 0;
    while (cut < groups.length - 1 && open < openBudget) { open += visibleWords(groups[cut]); cut += 1; }
    const rest = groups.slice(cut);
    if (rest.length >= 1 && cut >= 1) {
      const restMd = rest.join('\n\n');
      return [
        { kind: 'main', md: groups.slice(0, cut).join('\n\n') },
        { kind: 'fold', md: restMd, title: foldTitle(rest, visibleWords(restMd)), danger: false },
      ];
    }
  }
  const bulletIdx = lines.map((l, i) => (BULLET_RX.test(l) ? i : -1)).filter((i) => i >= 0);
  if (bulletIdx.length > keepBullets + 3) {
    const cut = bulletIdx[keepBullets];
    const tail = lines.slice(cut).join('\n');
    return [
      { kind: 'main', md: lines.slice(0, cut).join('\n') },
      { kind: 'fold', md: tail, title: bulletFoldTitle(tail, bulletIdx.length - keepBullets), danger: DANGER_RX.test(tail) },
    ];
  }
  return [{ kind: 'main', md }];
}

export function foldLeadInWarnings(md, question) {
  if (typeof md !== 'string' || !isLookupQuestion(question)) return [{ kind: 'main', md: md || '' }];
  const lines = md.split('\n');
  const segs = []; let main = []; let i = 0;
  const flush = () => { if (main.length) segs.push({ kind: 'main', md: main.join('\n') }); main = []; };
  while (i < lines.length) {
    if (LEADIN_RX.test(lines[i])) {
      let j = i + 1; while (j < lines.length && lines[j].trim() !== '') j += 1;
      const body = lines.slice(i, j).join('\n');
      flush(); segs.push({ kind: 'fold', md: body, title: 'Hinweise zur Sicherheit/Handhabung', danger: DANGER_RX.test(body) });
      i = j; continue;
    }
    main.push(lines[i]); i += 1;
  }
  flush();
  if (!segs.some((s) => s.kind === 'main' && s.md.trim())) return [{ kind: 'main', md }];
  return segs;
}

